import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";

import { baseApi } from "@/src/store/baseApi";
import type {
  AccountNotification,
  ActiveReferralPointsRequest,
  ActiveReferralPointsResponse,
  AddReferralRequest,
  AddReferralResponse,
  CitiesResponse,
  CityOption,
  CustomerProfile,
  ProfileApiError,
  UpdateProfileRequest,
  UpdateProfileResponse,
  WalletTransactionsResponse,
} from "./types";

const PROFILE_TAG = "Profile";
const PROFILE_CITIES_TAG = "ProfileCities";
const PROFILE_WALLET_TAG = "ProfileWallet";
const PROFILE_NOTIFICATIONS_TAG = "ProfileNotifications";

const profileApiWithTags = baseApi.enhanceEndpoints({
  addTagTypes: [
    PROFILE_TAG,
    PROFILE_CITIES_TAG,
    PROFILE_WALLET_TAG,
    PROFILE_NOTIFICATIONS_TAG,
  ],
});

export function extractProfileApiError(error: unknown) {
  const fetchError = error as
    | FetchBaseQueryError
    | {
        status?: number;
        data?: ProfileApiError;
      }
    | undefined;

  const status =
    typeof fetchError === "object" && fetchError && "status" in fetchError
      ? fetchError.status
      : undefined;
  const data =
    typeof fetchError === "object" && fetchError && "data" in fetchError
      ? (fetchError.data as ProfileApiError | undefined)
      : undefined;
  const message =
    data?.error ??
    data?.detail ??
    data?.message ??
    (typeof error === "object" &&
    error &&
    "error" in error &&
    typeof error.error === "string"
      ? error.error
      : null);

  return {
    status: typeof status === "number" ? status : undefined,
    data,
    message,
  };
}

export const profileQueryApi = profileApiWithTags.injectEndpoints({
  endpoints: (builder) => ({
    getProfile: builder.query<CustomerProfile, void>({
      query: () => ({
        url: "profile/me-customer",
        method: "GET",
      }),
      providesTags: [{ type: PROFILE_TAG, id: "CURRENT" }],
    }),
    updateProfile: builder.mutation<UpdateProfileResponse, UpdateProfileRequest>({
      query: (body) => ({
        url: "profile/update-profile",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: PROFILE_TAG, id: "CURRENT" }],
    }),
    getCities: builder.query<CityOption[], void>({
      query: () => ({
        url: "profile/cities",
        method: "GET",
      }),
      transformResponse: (response: CitiesResponse) => response.data,
      keepUnusedDataFor: 60 * 60 * 24,
      providesTags: [{ type: PROFILE_CITIES_TAG, id: "LIST" }],
    }),
    addReferral: builder.mutation<AddReferralResponse, AddReferralRequest>({
      query: (body) => ({
        url: "profile/add-referral",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: PROFILE_TAG, id: "CURRENT" }],
    }),
    activeReferralPoints: builder.mutation<
      ActiveReferralPointsResponse,
      ActiveReferralPointsRequest
    >({
      query: (body) => ({
        url: "profile/active-referral-points",
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: PROFILE_TAG, id: "CURRENT" },
        { type: PROFILE_WALLET_TAG, id: "CURRENT" },
      ],
    }),
    getWalletTransactions: builder.query<WalletTransactionsResponse, void>({
      query: () => ({
        url: "profile/wallet-transactions",
        method: "GET",
      }),
      keepUnusedDataFor: 30,
      providesTags: [{ type: PROFILE_WALLET_TAG, id: "CURRENT" }],
    }),
    getNotifications: builder.query<AccountNotification[], void>({
      query: () => ({
        url: "profile/notifications",
        method: "GET",
      }),
      transformResponse: (response: { data?: AccountNotification[] }) =>
        response.data ?? [],
      keepUnusedDataFor: 300,
      providesTags: [{ type: PROFILE_NOTIFICATIONS_TAG, id: "CURRENT" }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useActiveReferralPointsMutation,
  useAddReferralMutation,
  useGetCitiesQuery,
  useGetNotificationsQuery,
  useGetProfileQuery,
  useGetWalletTransactionsQuery,
  useLazyGetCitiesQuery,
  useLazyGetProfileQuery,
  useLazyGetWalletTransactionsQuery,
  useUpdateProfileMutation,
} = profileQueryApi;
